import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Disclaimer from "@/components/Disclaimer";
import { ExternalLink, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

// ------------------------------------------------------------------
// StatusBadge — two-state only:
//   - Under Legal Review (amber) when a row is disabled.
//   - Verified           (sage)  for everything else.
// The row's `last_verified_date` column already communicates recency;
// layering a categorical "Current vs Verified" split on top just added
// confusion (what threshold? why?). One consistent Verified badge is
// clearer and lets the date speak for itself.
// ------------------------------------------------------------------
const StatusBadge = ({ disabled, abbreviation }) => {
  if (disabled) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wide text-[var(--rg-amber)] border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)]" data-testid={`status-review-${abbreviation}`}>
        <AlertTriangle className="w-3.5 h-3.5" /> Under Legal Review
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wide text-[var(--rg-sage)] border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)]" data-testid={`status-verified-${abbreviation}`}>
      <CheckCircle2 className="w-3.5 h-3.5" /> Verified
    </span>
  );
};

const Row = ({ entry, kind }) => {
  const authority = kind === "tax" ? entry.authority_name : entry.enforcement_authority;
  return (
    <tr className="border-b border-[var(--rg-border)] hover:bg-[var(--rg-surface)]/60 transition-fast" data-testid={`data-row-${kind}-${entry.abbreviation}`}>
      <td className="py-3 px-4 text-[var(--rg-text-strong)] font-semibold">
        {entry.state_name}
        {entry.me_tooltip && (
          <span
            title={entry.me_tooltip}
            className="ml-2 inline-block px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-[var(--rg-amber-soft)] text-[var(--rg-amber)] border border-[var(--rg-amber)]/40 cursor-help"
            data-testid={`me-tooltip-${entry.abbreviation}`}
          >
            ISP-only — info
          </span>
        )}
      </td>
      <td className="py-3 px-4 text-[var(--rg-text)]">{authority}</td>
      <td className="py-3 px-4 text-[var(--rg-text)] font-mono text-xs">{entry.statute_citation}</td>
      <td className="py-3 px-4 text-[var(--rg-text)] font-mono text-xs">{entry.last_verified_date}</td>
      <td className="py-3 px-4"><StatusBadge disabled={entry.is_disabled} abbreviation={`${kind}-${entry.abbreviation}`} /></td>
      <td className="py-3 px-4">
        {entry.official_url ? (
          <a
            href={entry.official_url}
            target="_blank"
            rel="noopener noreferrer"
            title={entry.official_url}
            className="text-[var(--rg-sage)] hover:underline inline-flex items-center gap-1 text-xs font-semibold max-w-[180px] truncate"
            data-testid={`data-source-${kind}-${entry.abbreviation}`}
          >
            <span className="truncate">Official Source ↗</span>
          </a>
        ) : (
          <span className="text-[var(--rg-muted)] text-xs italic">No statute (Monitoring)</span>
        )}
      </td>
    </tr>
  );
};

const SectionTable = ({ title, kind, rows, testId }) => (
  <div className="mt-10" data-testid={testId}>
    <div className="flex items-baseline justify-between mb-3">
      <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)]">{title}</h2>
      <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--rg-muted)] font-bold">{rows.length} jurisdictions</span>
    </div>
    <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[var(--rg-muted)] border-b border-[var(--rg-border)]">
            <th className="py-3 px-4">Jurisdiction</th>
            <th className="py-3 px-4">Authority</th>
            <th className="py-3 px-4">Statute Citation</th>
            <th className="py-3 px-4">Last Verified</th>
            <th className="py-3 px-4">Status</th>
            <th className="py-3 px-4">Source</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => <Row key={`${kind}-${r.abbreviation}`} entry={r} kind={kind} />)}
        </tbody>
      </table>
    </div>
  </div>
);

export default function DataVerification() {
  const [data, setData] = useState({ tax: [], privacy: [] });
  const [dbVersion, setDbVersion] = useState("v1.0");
  // DC privacy has no enacted statute; force null URL.
  // ME privacy: ISP-only law — display custom values for clarity.
  useEffect(() => {
    api.get("/public/jurisdictions").then((r) => {
      const privacy = (r.data.privacy || []).map((p) => {
        if (p.abbreviation === "DC") return { ...p, official_url: null };
        if (p.abbreviation === "ME") {
          return {
            ...p,
            enforcement_authority: "Maine Attorney General",
            statute_citation: "N/A",
            official_url: "https://www.maine.gov/ag",
            me_tooltip: "Maine's ISP privacy law applies to internet service providers only — not to Shopify e-commerce merchants. No comprehensive consumer privacy law applies to general merchants in Maine.",
          };
        }
        return p;
      });
      setData({ tax: r.data.tax || [], privacy });
    }).catch(() => {});
    api.get("/public/database-version").then((r) => setDbVersion(r.data.database_version || "v1.0")).catch(() => {});
  }, []);

  // Compute the most-recent last_verified_date across all active rows
  const lastVerifiedInfo = useMemo(() => {
    const all = [...data.tax, ...data.privacy].filter((r) => !r.is_disabled && r.last_verified_date);
    if (all.length === 0) return { mostRecent: null, nextReview: null };
    const sorted = [...all].sort((a, b) => (b.last_verified_date > a.last_verified_date ? 1 : -1));
    const mostRecent = sorted[0].last_verified_date;
    // Add 7 days
    const d = new Date(mostRecent + "T00:00:00Z");
    if (!isNaN(d.getTime())) {
      d.setUTCDate(d.getUTCDate() + 7);
      const nextReview = d.toISOString().slice(0, 10);
      return { mostRecent, nextReview };
    }
    return { mostRecent, nextReview: null };
  }, [data]);

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Trust · Statute Verification</div>
        <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl text-[var(--rg-text-strong)] mb-3">Regulens Data Verification Dashboard</h1>
        <p className="text-[var(--rg-text)] mb-2 max-w-3xl">
          Every data point in Regulens traces to an official government source. Below is each jurisdiction's statute verification record — split into Sales Tax Nexus rules and State Privacy Laws — with the date it was last checked against the official source.
        </p>
        <p className="text-xs text-[var(--rg-muted)] font-mono mb-2">Each row shows the exact date its statute was last checked against the official government source. Click any source link to inspect the government record directly.</p>

        {/* P4 — Last-verified banner */}
        {lastVerifiedInfo.mostRecent && (
          <div className="mt-6 rounded-md border-l-4 border-l-[var(--rg-sage)] border border-[var(--rg-border)] bg-[var(--rg-surface)]/60 px-4 py-3 flex items-start gap-3" data-testid="data-verification-banner">
            <ShieldCheck className="w-4 h-4 text-[var(--rg-sage)] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-[var(--rg-text)] font-mono">
              <strong className="text-[var(--rg-text-strong)]">Database last verified:</strong> {lastVerifiedInfo.mostRecent}
              {lastVerifiedInfo.nextReview && (<> · <strong className="text-[var(--rg-text-strong)]">Next scheduled review:</strong> {lastVerifiedInfo.nextReview}</>)}
              {" "}· <strong className="text-[var(--rg-text-strong)]">Database version:</strong> {dbVersion}
            </div>
          </div>
        )}

        <SectionTable title="Sales Tax Nexus Rules" kind="tax" rows={data.tax} testId="section-sales-tax" />
        <SectionTable title="State Privacy Laws" kind="privacy" rows={data.privacy} testId="section-privacy-law" />

        <p className="text-xs text-[var(--rg-muted)] mt-6 font-mono">This page updates each time our team completes a weekly review.</p>
        <div className="mt-8"><Disclaimer /></div>
      </section>
    </Layout>
  );
}
